import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSimulation } from "@/context/SimulationContext";
import api from "@/lib/api";
import { LayoutDashboard, Ticket, Settings, ShieldCheck, LogOut, Menu, X, Plus, Users, UserPlus, History, Activity, MessageCircle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_HORIZONTAL, SUPPORT_WHATSAPP } from "@/lib/brand";
import { isOwner, can, effOf } from "@/lib/perms";

const nav = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard, exact: true },
  { to: "/dashboard/clientes", label: "Clientes", icon: Users },
  { to: "/dashboard/settings", label: "Marca", icon: Settings },
];

export default function DashboardLayout({ children, title, action }) {
  const { user, logout } = useAuth();
  const { sim, stopSim, updatePerms } = useSimulation();
  const [permOpen, setPermOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (user?.is_super_admin) {
      const fetchCounts = () => api.get("/admin/pending-counts").then((r) => setPendingCount(r.data.total || 0)).catch((e) => console.error("No se pudo cargar el conteo pendiente:", e));
      fetchCounts();
      window.addEventListener("refresh-pending-counts", fetchCounts);
      return () => window.removeEventListener("refresh-pending-counts", fetchCounts);
    }
  }, [user, location.pathname]);

  // Auto-cierre de sesión del super-admin tras 30 min de inactividad (evita dejar el panel abierto por error)
  useEffect(() => {
    if (!user?.is_super_admin) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { logout(); }, 30 * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const eff = sim || user;
  const sa = !sim && !!user?.is_super_admin;
  const owner = sim ? true : isOwner(user);
  const feat = effOf(user, sim);
  const plan = feat.plan;
  const canHistory = sa || !!feat.can_view_history;
  const canTeam = sa || (feat.max_assistants > 0);
  const canActivity = sa || !!feat.can_view_history;
  const items = [
    { to: "/dashboard", label: "Panel", icon: LayoutDashboard, exact: true },
  ];
  if (canHistory) items.push({ to: "/dashboard/historial", label: "Historial", icon: History });
  if (canActivity) items.push({ to: "/dashboard/actividad", label: "Actividad", icon: Activity });
  const canCrm = sa || owner || !!user?.crm_access;
  if (canCrm) items.push({ to: "/dashboard/clientes", label: "Clientes", icon: Users });
  if (owner && canTeam) items.push({ to: "/dashboard/equipo", label: "Equipo", icon: UserPlus });
  if (owner) items.push({ to: "/dashboard/settings", label: "Marca", icon: Settings });
  if (user?.is_super_admin && !sim) items.push({ to: "/gestion-interna-rifa-17", label: "Super-Admin", icon: ShieldCheck });

  const toggleSimPerm = async (k) => {
    if (!sim) return;
    const val = !(sim.custom_perms?.[k]);
    updatePerms({ [k]: val });
    try { await api.put(`/admin/organizers/${sim.user_id}`, { plan: "custom", custom_perms: { ...sim.custom_perms, [k]: val } }); }
    catch (e) { console.error("No se pudo actualizar el permiso:", e); }
  };

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const Sidebar = (
    <aside className="w-64 bg-white border-r border-neutral-200 flex flex-col h-full">
      <div className="p-6 border-b border-neutral-100">
        <a href="/dashboard" data-testid="sidebar-logo-link" className="flex items-center gap-2">
          <img src={LOGO_HORIZONTAL} alt="MCTECH EVENTOS" className="h-7 w-auto" />
          <span className="font-display font-extrabold text-sm tracking-tight">EVENTOS</span>
        </a>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive(item) ? "bg-[#E6F7FE] text-[#0092CC]" : "text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              <Icon size={18} /> <span className="flex-1">{item.label}</span>
              {item.to === "/gestion-interna-rifa-17" && pendingCount > 0 && (
                <span data-testid="admin-pending-count" className="ml-auto text-xs font-bold min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white flex items-center justify-center">{pendingCount}</span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-neutral-100">
        <div className="flex items-center gap-3 px-3 py-2">
          <img src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "U")}`} alt="" className="w-8 h-8 rounded-full" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-neutral-400 truncate">{user?.email}</p>
          </div>
        </div>
        <Button variant="ghost" data-testid="logout-btn" onClick={logout} className="w-full justify-start text-neutral-600 mt-1">
          <LogOut size={18} className="mr-2" /> Cerrar sesión
        </Button>
      </div>
    </aside>
  );

  return (
    <div className={`min-h-screen bg-neutral-50 flex ${sim ? "pt-12" : ""}`}>
      {sim && (
        <div data-testid="simulation-bar" className="fixed top-0 left-0 right-0 z-[60] h-12 bg-neutral-900 text-white flex items-center gap-3 px-4 shadow-lg">
          <Eye size={16} className="text-[#00AEEF] shrink-0" />
          <span className="text-sm font-semibold truncate">Simulando: <span className="text-[#00AEEF]">{sim.name}</span></span>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Button data-testid="sim-edit-perms-btn" size="sm" variant="outline" onClick={() => setPermOpen((v) => !v)} className="rounded-lg h-8 border-white/30 text-white hover:bg-white/10 bg-transparent">Editar Permisos</Button>
              {permOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-xl bg-white text-neutral-800 shadow-xl border border-neutral-200 p-2 z-[70]" data-testid="sim-perms-popup">
                  {[["can_create_assistants", "Asistentes"], ["can_download_csv", "CSV clientes"], ["can_view_history", "Historial"], ["adv_stats", "Estadísticas avanzadas"], ["white_label", "Marca blanca"], ["qr_tickets", "Tickets con QR"]].map(([k, lbl]) => {
                    const on = !!sim.custom_perms?.[k];
                    return (
                      <button key={k} data-testid={`sim-perm-${k}`} onClick={() => toggleSimPerm(k)} className="w-full flex items-center justify-between rounded-md px-2.5 py-2 text-sm hover:bg-neutral-50">
                        <span>{lbl}</span>
                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? "bg-[#00AEEF]" : "bg-neutral-300"}`}><span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} /></span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <Button data-testid="sim-exit-btn" size="sm" onClick={() => { setPermOpen(false); stopSim(); }} className="rounded-lg h-8 bg-[#00AEEF] hover:bg-[#0092CC]">Salir de Simulación</Button>
          </div>
        </div>
      )}
      <div className="hidden md:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-neutral-200 flex items-center justify-between px-4 md:px-8 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="md:hidden -ml-2 w-11 h-11 rounded-xl flex items-center justify-center text-neutral-700 active:bg-neutral-100 transition-colors" onClick={() => setOpen(true)} data-testid="open-menu-btn" aria-label="Abrir menú"><Menu /></button>
            <h1 className="font-display font-bold text-lg md:text-xl">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {action}
            {can(user, "manage_raffles") && (
              <Button size="sm" data-testid="new-raffle-header-btn" onClick={() => navigate("/dashboard/raffles/nueva")} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl">
                <Plus size={16} className="mr-1" /> Nueva rifa
              </Button>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 w-full max-w-7xl mx-auto">{children}</main>
      </div>
      {(
        <a
          data-testid="support-whatsapp-btn"
          href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent("¿Necesitas ayuda?")}`}
          target="_blank" rel="noreferrer"
          aria-label="¿Necesitas ayuda?"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 h-12 pl-3.5 pr-4 rounded-full bg-[#25D366] text-white shadow-lg hover:scale-105 active:scale-95 transition-transform"
        >
          <MessageCircle size={20} />
          <span className="hidden sm:inline text-sm font-semibold whitespace-nowrap">¿Necesitas ayuda?</span>
        </a>
      )}
    </div>
  );
}
